<template>
  <div class="setup">
    <div class="content-box centered">
      <div class="logo">
        <img src="~@/assets/connected-radio-logo.svg">
      </div>
    </div>
    <div class="content-box">
      <div class="setup-form layout">
        <h4>Specify layout properties</h4>
        <form-item label="Layout type">
          <layout-type-select
            :layouts="layouts"
            :selected="selectedLayout"
            @select="setLayout"
          />
        </form-item>
        <form-item label="Layout preview" class="layout-preview">
          <layout-preview
            :layout="selectedLayout.name"
            :markets="selectedLayout.marketCount"
            :orientation="selectedLayout.orientation"
            :broadcasts="selectedLayout.tileCount"
          />
        </form-item>
      </div>
    </div>
    <div v-if="selectedLayout.marketCount" class="content-box">
      <div class="setup-form markets">
        <h4>Choose markets</h4>
        <form-item
          v-for="(market, n) in selectedMarkets"
          :key="n"
          :label="`Market ${n+1}`"
        >
          <market-selector
            :custom-markets="customMarkets"
            :static-markets="staticMarkets"
            :value="market"
            @add="createCustomMarket(n)"
            @edit="updateCustomMarket"
            @remove="removeCustomMarket"
            @export="exportCustomMarket"
            @uploaded="importCustomMarkets"
            @upload-errored="uploadFailed"
            @selected="value => selectMarket({ market: value, index: n })"
          />
        </form-item>
      </div>
    </div>
    <div class="content-box centered">
      <div class="setup-form audio">
        <h4>Enable audio streaming</h4>
        <switches v-model="audio" />
      </div>
      <url-generator
        label="Generate Panels"
        class="apply-button"
        :layout="selectedLayout.name"
        :markets="selectedMarketIds"
        :audio="audio"
      />
    </div>
    <dash-modal
      name="upload-success"
      header="Successfully uploaded"
    >
      <div slot="contents" class="message">
        New custom market configurations added to market list
      </div>
    </dash-modal>
    <dash-modal
      name="invalid-files"
      header="Wrong file formats"
    >
      <div slot="contents" class="message">
        Some of the files you are uploading does not belong to custom markets.
        Please, upload .json files with market configuration
      </div>
    </dash-modal>
    <dash-modal
      name="upload-error"
      header="Upload failed"
    >
      <div slot="contents" class="message">
        Something went wrong and files have not been successfully loaded. Please, try later.
      </div>
    </dash-modal>
    <confirm-remove-modal ref="removeModal" />
  </div>
</template>

<script>
import { snakeCase } from 'lodash';
import {
  mapState, mapGetters, mapActions, mapMutations,
} from 'vuex';

/* eslint-disable no-multi-spaces */
import saveFile           from 'file-saver';
import Switches           from 'vue-switches';
import FormItem           from '@/components/common/FormItem';
import MarketSelector     from '@/components/common/MarketSelector';
import DashModal          from '@/components/common/DashModal';
import LayoutTypeSelect   from './LayoutTypeSelect';
import UrlGenerator       from './UrlGenerator';
import LayoutPreview      from './LayoutPreview';
import ConfirmRemoveModal from './_ConfirmRemoveModal';
/* eslint-enable no-multi-spaces */

export default {
  name: 'Setup',
  components: {
    Switches,
    FormItem,
    MarketSelector,
    LayoutTypeSelect,
    UrlGenerator,
    LayoutPreview,
    DashModal,
    ConfirmRemoveModal,
  },
  data() {
    return {
      audio: false,
    };
  },
  computed: {
    ...mapGetters('setup', ['selectedMarketIds']),
    ...mapState('setup', [
      'customMarkets', 'staticMarkets', 'selectedMarkets', 'layouts', 'selectedLayout',
    ]),
  },
  watch: {
    audio: {
      immediate: true,
      handler() {
        this.setAudio(this.audio);
      },
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...mapActions('setup', ['load']),
    ...mapActions('setup', {
      removeMarket: 'removeCustomMarket',
      importMarket: 'importCustomMarket',
    }),
    ...mapMutations('setup', ['setLayout', 'selectMarket', 'setAudio', 'setPendingMarketInput']),
    createCustomMarket(index) {
      this.setPendingMarketInput(index);
      this.$router.push({ name: 'create-custom-market' });
    },
    updateCustomMarket({ id }) {
      this.$router.push({ name: 'update-custom-market', params: { marketId: id } });
    },
    async removeCustomMarket({ id, name }) {
      if (await this.$refs.removeModal.confirm(name)) this.removeMarket(id);
    },
    importCustomMarkets(configs) {
      const isValid = configs.every((config) => {
        const { id, name, broadcasts } = config;
        return id && name && broadcasts;
      });
      if (!isValid) this.$modal.show('invalid-files');
      else {
        configs.forEach(config => this.importMarket(config));
        this.$modal.show('upload-success');
      }
    },
    uploadFailed(error) {
      if (error.message === 'Invalid file format') this.$modal.show('invalid-files');
      else this.$modal.show('upload-error');
    },
    exportCustomMarket(market) {
      const file = new Blob([JSON.stringify(market)], { type: 'application/json;charset=utf-8' });
      saveFile(file, snakeCase(market.name));
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/variables";
@import "~@/assets/scss/layout";

.setup {
  min-width: 320px;
  width: 100%;
  height: 100%;
  color: $grey;
  font-family: 'Lato', 'Univers', Helvetica, Arial, sans-serif;
}

.content-box {
  width: 100%;

  &:not(:first-child) {
    border-top: solid 1px $charcoal-dark;
  }
}

.centered {
  text-align: center;
}

.logo {
  width: 260px;
  height: 80px;
  margin: 40px auto;
  object-fit: contain;
  display: inline-block;

  img {
    width: 100%;
  }
}

.setup-form {
  width: 820px;
  margin: 0 auto;
  padding: 50px 20px 44px;
  text-align: left;
  display: grid;

  &.layout {
    grid-template-columns: 1fr 480px;
    grid-gap: 20px 60px;
  }

  &.markets {
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px 40px;
  }
}

h4 {
  font-size: 25px;
  font-weight: $weight-black;
  color: $white;
  margin: 0 0 40px 0;
  grid-column: 1 /-1;
}

.apply-button {
  grid-column: 1 /-1;
  margin: 40px auto;
}

.audio {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4 {
    margin: 0 30px 0 0;
  }

  .vue-switcher-theme--default.vue-switcher-color--default {
    &.vue-switcher--unchecked > div {
      background-color: $charcoal-light;
    }

    & > div {
      background-color: $orange;
    }

    &.vue-switcher--unchecked > div:after {
      background-color: $white;
    }

    & > div:after {
      background-color: $white;
    }
  }

}

@media screen and (max-width: 860px) {
  .setup-form {
    width: 480px;
      &.layout {
        grid-template-columns: 1fr;
        grid-gap: 20px;
      }

      &.markets {
        grid-template-columns: 1fr;
        grid-gap: 10px;
      }
  }
}

@media screen and (max-width: 520px) {
  .setup-form {
    width: 100%;
    box-sizing: border-box;
  }
}
</style>
